<script setup>
import { Dialog, DialogPanel } from "@headlessui/vue";
import { computed, defineEmits, defineProps, toRefs } from "vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";

const emit = defineEmits(["closeModal", "updatePrice", "keepOriginalPrice", "goOn"]);

import { usePrifeDifference } from "@/components/StudentFulfillment/composables/priceDifference.js";
import { formattedPriceFromCentsWithoutCents } from "@/util/formatter";

const props = defineProps({
  isModalOpen: {
    type: Boolean,
    default: false,
  },
  roomsDiff: {
    type: Object,
    required: false,
  },
  ordersDiff: {
    type: Object,
    required: false,
  },
  durationsDiff: {
    type: Object,
    required: false,
  },
});

const { roomsDiff, ordersDiff, durationsDiff } = toRefs(props);

const roomOriginal = computed(() => roomsDiff?.value?.original ?? {});
const roomUpdated = computed(() => roomsDiff?.value?.updated ?? {});
const originalSessionPrices = computed(() => ordersDiff?.value?.order_base_price ?? {});
const updatedOrder = computed(() => ordersDiff?.value?.updated ?? {});

const { getDiffs } = usePrifeDifference();
const diffs = computed(() => {
  if (originalSessionPrices?.value || updatedOrder?.value) {
    return getDiffs({
      left: originalSessionPrices?.value,
      right: updatedOrder?.value,
    });
  }
  return undefined;
});

const hasChangedClass = "text-warning-900";
const isTotalDifferent = computed(() => {
  return originalSessionPrices?.value?.total_price_in_cents !== updatedOrder?.value?.total_price_in_cents;
});

const isSpecialPrice = computed(() => {
  return updatedOrder?.value?.orig_total_price_in_cents > 0;
});

const closeModal = () => {
  emit("closeModal");
};

const updatePrice = () => {
  emit("updatePrice");
};

const keepOriginalPrice = () => {
  emit("keepOriginalPrice");
};
const goOn = () => {
  emit("goOn");
};
const getDurationText = (type, value) => {
  if (!value) {
    return `${type} duration is unknown. Proceed with caution.`;
  }
  return `for ${value} days`;
};
const formatHousingValue = (duration, price) => {
  if (!duration) return price ?? "";
  return formattedPriceFromCentsWithoutCents(Number(duration) * Number(price.replace("$", "") * 100));
};
</script>

<template>
  <div>
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-[1200] hidden bg-university-primary opacity-50 sm:block"
    ></div>
    <Dialog
      :open="isModalOpen"
      class="fixed inset-0 z-[1200] py-6 overflow-auto bg-white sm:bg-transparent sm:p-8"
    >
      <div
        class="p-6 w-fit h-fit mx-auto bg-white sm:rounded-lg sm:shadow z-40 relative"
      >
        <DialogPanel>
          <section class="sm:px-6 md:px-12 sm:py-10 h-fit">
            <h1
              class="flex justify-center text-xl leading-tight text-center sm:text-2xl md:text-3xl"
            >
              Price Difference
            </h1>
            <div
              class="flex flex-col text-center mt-8 mb-8"
              v-if="roomOriginal?.roomPrice"
            >
              <p>
                The price of the bed you chose is different from the price of
                the requested room. How would you like to proceed?
              </p>
            </div>
            <div
              class="md:grid md:grid-cols-2 mt-8 mb-8 md:gap-8 md:space-y-0 space-y-2"
            >
              <div class="space-y-2 min-w-80">
                <div class="!h-21" v-if="roomOriginal?.roomPrice">
                  <div>
                    <span class="text-blue-700 mb-4 font-semibold"
                      >Original Room</span
                    >
                  </div>
                  <div class="grid grid-cols-3">
                    <div class="text-blue-700 max-w-xs break-words col-span-2">
                      {{ roomOriginal?.housingName ?? "" }},
                      {{ roomOriginal?.housingTier ?? "" }},
                      {{ roomOriginal?.roomType ?? "" }}
                      {{
                        getDurationText(
                          "Original",
                          durationsDiff?.order_duration
                        )
                      }}
                    </div>
                    <div class="flex items-end justify-end pr-6 col-span-1">
                      <span class="font-semibold text-blue-700">{{
                        roomOriginal?.roomPrice ?? ""
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="!mt-8 space-y-2">
                  <div>
                    <span class="text-blue-700 mb-4 font-semibold"
                      >Program Session Base Price</span
                    >
                  </div>
                  <div
                    v-if="diffs?.educationalFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.educationalFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.educationalFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.internshipFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.internshipFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.internshipFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.housingFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.housingFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.housingFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.excursionsFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.excursionsFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.excursionsFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.insuranceFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.insuranceFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.insuranceFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.groupFlightFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.groupFlightFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.groupFlightFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.transportationFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.transportationFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.transportationFee?.original ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.classesFee?.show"
                    class="flex justify-between pr-6"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.classesFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.classesFee?.original ?? "" }}</div>
                  </div>
                </div>
              </div>
              <div>
                <div class="!h-21" v-if="roomOriginal?.roomPrice">
                  <div>
                    <span class="text-red-700 mb-4 font-semibold"
                      >Updated Room</span
                    >
                  </div>
                  <div class="grid grid-cols-3">
                    <p class="text-red-700 max-w-xs break-words col-span-2">
                      {{ roomUpdated?.housingName ?? "" }},
                      {{ roomUpdated?.housingTier ?? "" }},
                      {{ roomUpdated?.roomType ?? "" }}
                      {{
                        getDurationText(
                          "Original",
                          durationsDiff?.session_duration
                        )
                      }}
                    </p>
                    <div class="flex items-end justify-end pr-6 col-span-1">
                      <span class="font-semibold text-red-700">{{
                        roomUpdated?.roomPrice ?? ""
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="!mt-8 space-y-2">
                  <div>
                    <span class="text-red-700 mb-4 font-semibold space-y-4"
                      >Updated Session Price</span
                    >
                  </div>
                  <div
                    v-if="diffs?.educationalFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.educationalFee?.hasChanged ? hasChangedClass : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.educationalFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.educationalFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.internshipFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.internshipFee?.hasChanged ? hasChangedClass : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.internshipFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.internshipFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.housingFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.housingFee?.hasChanged ? hasChangedClass : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.housingFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.housingFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.excursionsFee?.show"
                    class="flex justify-between pr-6"
                    :class="[
                      'flex justify-between pr-6',
                      diffs?.excursionsFee?.hasChanged ? hasChangedClass : '',
                    ]"
                  >
                    <!-- Description -->
                    <div>{{ diffs?.excursionsFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.excursionsFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.insuranceFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.insuranceFee?.hasChanged ? hasChangedClass : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.insuranceFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.insuranceFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.groupFlightFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.groupFlightFee?.hasChanged ? hasChangedClass : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.groupFlightFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.groupFlightFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.transportationFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.transportationFee?.hasChanged
                        ? hasChangedClass
                        : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.transportationFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.transportationFee?.updated ?? "" }}</div>
                  </div>
                  <div
                    v-if="diffs?.classesFee?.show"
                    class="flex justify-between pr-6"
                    :class="
                      diffs?.classesFee?.hasChanged ? hasChangedClass : ''
                    "
                  >
                    <!-- Description -->
                    <div>{{ diffs?.classesFee?.name ?? "" }}</div>
                    <!-- Values -->
                    <div>{{ diffs?.classesFee?.updated ?? "" }}</div>
                  </div>

                  <div v-for="addon in ordersDiff.addons">
                    <div v-if="addon" class="flex justify-between pr-8">
                      <div>{{ addon?.product_name }}</div>
                      <div>
                        {{
                          formattedPriceFromCentsWithoutCents(
                            addon?.total_price_in_cents
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-2 mb-8 md:gap-8">
              <div class="flex justify-between pr-6">
                <div class="font-bold">Total</div>
                <div class="font-bold">
                  {{
                    formattedPriceFromCentsWithoutCents(
                      originalSessionPrices?.total_price_in_cents
                    )
                  }}
                </div>
              </div>
              <div
                class="flex justify-between pr-6"
                :class="isTotalDifferent ? hasChangedClass : ''"
              >
                <div class="font-bold">Total</div>
                <div class="font-bold">
                  {{
                    formattedPriceFromCentsWithoutCents(
                      isSpecialPrice
                        ? updatedOrder?.student_total_price_in_cents
                        : updatedOrder?.total_price_in_cents
                    )
                  }}
                </div>
              </div>
            </div>
            <div
              v-if="roomOriginal?.roomPrice"
              class="flex align-center justify-center h-10 flex-0"
            >
              <BaseButton
                outlined
                :on-click="closeModal"
                class="px-7 font-semibold"
                >Cancel</BaseButton
              >
              <BaseButton
                :on-click="keepOriginalPrice"
                class="font-semibold"
                :disabled="true"
                >Keep the original price</BaseButton
              >
              <BaseButton :on-click="updatePrice" class="font-semibold"
                >Update</BaseButton
              >
            </div>
            <div v-else class="flex align-center justify-center h-10 flex-0">
              <BaseButton
                outlined
                :on-click="closeModal"
                class="px-7 font-semibold"
                >Cancel</BaseButton
              >
              <BaseButton :on-click="goOn" class="font-semibold"
                >Continue</BaseButton
              >
            </div>
          </section>
        </DialogPanel>
      </div>
    </Dialog>
  </div>
</template>
